/**
 * Login page.
 *
 * Copyright © Ircam Amplify 2024-2026
 */
import { fetchInitialBatch } from '@/api'
import AWSWAFCaptchaModal from '@/components/AWSCaptchaModal'
import FormReturnHandler from '@/components/FormReturnHandler'
import '@/components/RhfFormInput.css'
import { resetLayoutSlice, resetUserSlice, store } from '@/store'
import { clearApiCache } from '@amplify3/duplo-back-end'
import { signIn } from 'aws-amplify/auth'
import { cognitoUserPoolsTokenProvider } from 'aws-amplify/auth/cognito'
import { defaultStorage, sessionStorage } from 'aws-amplify/utils'
import { useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import './Login.css'

import { RhfFormInput, RhfFormInputPassword } from '@/components/RhfFormInput'
import { zodResolver } from '@hookform/resolvers/zod'
import { useForm } from 'react-hook-form'
import { z as zod } from 'zod'
import LogInButton from './LoginButton'
import LoginDialog from './LoginDialog'

/**
 * The Zod schema defining the fields and their data type.
 */
const LogInSchema = zod.object({
  email: zod.email().trim(),
  password: zod.string().min(1, 'Required'),
  stayConnected: zod.boolean(),
})
type LogInType = zod.infer<typeof LogInSchema>

export async function signInUser() {
  clearApiCache()
  fetchInitialBatch().catch((error) => {
    console.log('Error in fetchInitialBatch:', error)
  })
}

export async function signOutUser() {
  clearApiCache()
  store.dispatch(resetUserSlice())
  store.dispatch(resetLayoutSlice())
}

export const LoginPage = () => {
  const navigate = useNavigate()
  const location = useLocation()

  // Get the needed functions defined by react-use-form
  const {
    register,
    control,
    handleSubmit,
    watch,
    formState: { errors, isValid },
  } = useForm<LogInType>({
    mode: 'all',
    resolver: zodResolver(LogInSchema),
  })

  const justSignedUp = location?.state?.justSignedUp || false
  const [errorMessage, setErrorMessage] = useState(null)
  const [loading, setLoading] = useState(false)
  const email = watch('email')

  const submitLogin = async (data: LogInType) => {
    setLoading(true)
    try {
      if (data.stayConnected)
        cognitoUserPoolsTokenProvider.setKeyValueStorage(defaultStorage)
      else cognitoUserPoolsTokenProvider.setKeyValueStorage(sessionStorage)
      const cognitoRes = await signIn({
        username: data.email,
        password: data.password,
      })
      if (!cognitoRes.isSignedIn) {
        if (
          cognitoRes.nextStep &&
          cognitoRes.nextStep.signInStep === 'CONFIRM_SIGN_UP'
        ) {
          throw new Error(
            'Please check your inbox and click the verification link. ' +
              'The email may take a few minutes to arrive.'
          )
        } else {
          throw new Error(
            'Unknown error while login in. Please try again or contact support.'
          )
        }
      }
      await signInUser()
      navigate('/')
    } catch (e: any) {
      if (e.name === 'Unknown') {
        window.shouldShowCaptcha = true
        await submitLogin(data)
      } else {
        console.error(e)
        setErrorMessage(e.message)
        setLoading(false)
      }
    }
  }
  const authenticate = handleSubmit(submitLogin)

  return (
    <>
      {justSignedUp && (
        <FormReturnHandler
          success
          message={
            'An email has been sent to you with a link ' +
            'to activate your account'
          }
          onClose={() => {}}
          open={justSignedUp}
        />
      )}

      <div className="login-page">
        <h1 className="login-title">Log In</h1>
        <form onSubmit={authenticate} className="rhf-form">
          <RhfFormInput
            title="Email address"
            id="email"
            type="string"
            register={register}
            error={errors.email}
          />

          <RhfFormInputPassword
            title="Password"
            id="password"
            register={register}
            error={errors.password}
            control={control}
          />

          <div className="login-submit-container">
            <RhfFormInput
              id="stayConnected"
              register={register}
              error={errors.stayConnected}
              type="checkbox"
              inputText="Stay connected"
            />

            <LogInButton
              text={loading ? 'Logging In ...' : 'Log In'}
              type="submit"
              disabled={loading || !isValid}
            />

            <Link
              className="login-forgot-password-link"
              to={'/forgot-password?email=' + encodeURIComponent(email)}
            >
              Forgot your password?
            </Link>
          </div>
        </form>
      </div>

      <LoginDialog
        title="Error while logging in"
        message={errorMessage}
        onClose={() => {
          setErrorMessage(null)
        }}
        onClick={() => {
          setErrorMessage(null)
        }}
        open={errorMessage !== null}
      />

      <AWSWAFCaptchaModal />
    </>
  )
}
